import { Inject, Injectable } from '@angular/core';
import {
  TREQUEST_APP_MATER,
  TREQUEST_APP_SETTING,
  TREQUEST_GET_UPDATES,
  TREQUEST_REGISTERED_APP,
  TREQUEST_TENANT_APPS,
  TRESPONSE_APP_MASTER,
  TRESPONSE_GET_UPDATES,
  TRESPONSE_PRE_SETTINGS,
  TRESPONSE_REGISTERED_APP,
  TRESPONSE_TENANT_APPS,
} from '@monorepo/types';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EnvironmentService } from './environment.service';
import * as _ from 'lodash';
import { TPreSettingCheckedList } from '@monorepo/resolvers';
import * as moment from 'moment-timezone';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import { BuildUtils } from '@monorepo/utils';
import { Device, DeviceInfo } from '@capacitor/device';
import { CapacitorHttpService } from '@monorepo/services';
import { JwtHelperService } from '@auth0/angular-jwt';
import { DeviceId } from '../device-id/device-id';
export const tzdetect = {
  names: moment.tz.names(),
  matches: function (base: any) {
    const results: any[] = [],
      now = Date.now(),
      makekey = function (id: any) {
        return [0, 4, 8, -5 * 12, 4 - 5 * 12, 8 - 5 * 12, 4 - 2 * 12, 8 - 2 * 12]
          .map(function (months) {
            const m = moment(now + months * 30 * 24 * 60 * 60 * 1000);
            if (id) {
              m.tz(id);
            }
            return m.format('DDHHmm');
          })
          .join(' ');
      },
      lockey = makekey(base);
    tzdetect.names.forEach(function (id) {
      if (makekey(id) === lockey) {
        results.push(id);
      }
    });
    return results;
  },
};

@Injectable({
  providedIn: 'root',
})
export class ActivationService {
  private deviceId!: string;
  private deviceInfo!: DeviceInfo;

  constructor(
    private http: HttpClient,
    private persistentStorageService: PersistentStorageService,
    private capacitorHttpService: CapacitorHttpService,
    @Inject(EnvironmentService) private envProvider: EnvironmentService,
  ) {
    // Populate real values asynchronously (native and web)
  }

  async isActivated() {
    const appMaster = await this.persistentStorageService.get('[APP_MASTER]');
    // TODO: Fix using authenticatoin key
    if (appMaster) {
      return 'ACTIVATED_APP_INSTALLED' as const;
    } else {
      return 'ACTIVATION_REQUIRED' as const;
    }
  }

  getDeviceId() {
    return this.deviceId;
  }

  getDeviceInfo() {
    return this.deviceInfo;
  }
  getTenantInfo(postToken: TREQUEST_TENANT_APPS) {
    const url = this.envProvider.getLicenceUrl('VALIDATE_TOKEN');
    return this.http.post<TRESPONSE_TENANT_APPS>(url, postToken);
  }

  getMasterAppConfig(tokenInfo: TREQUEST_TENANT_APPS, appMaster: TRESPONSE_TENANT_APPS['app_masters'][0]) {
    return this.getMasterAppConfigByTokenAndAppMasterId({
      token: tokenInfo.token,
      app_master_id: appMaster.app_master_id,
    });
  }

  getMasterAppConfigByTokenAndAppMasterId(payload: { token: string; app_master_id: string }) {
    const url = this.envProvider.getLicenceUrl('GET_APP_MASTER');
    const postBody: TREQUEST_APP_MATER = {
      act_tkn: payload.token,
      appmasterid: payload.app_master_id,
    };
    return this.http.post<TRESPONSE_APP_MASTER>(url, JSON.stringify(postBody), {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    });
  }

  private getAppContextByType<T>(
    appMaster: TRESPONSE_APP_MASTER,
    clientInfo: TRESPONSE_TENANT_APPS['client_info'],
    context: TREQUEST_APP_SETTING['context'],
  ) {
    const url = this.envProvider.getCoreServiceUrl('SUPPORTING_DATA');
    const ptypes = _.chain(appMaster.apps)
      .map((v) => v.product_type)
      .value();
    const msgBody: TREQUEST_APP_SETTING = {
      tenant: clientInfo.tenant,
      subject_key: clientInfo.subject_key,
      app_types: ptypes,
      context,
    };
    return this.http.post<T>(url, JSON.stringify(msgBody), {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    });
  }

  getPreSettingsData(appMaster: TRESPONSE_APP_MASTER, clientInfo: TRESPONSE_TENANT_APPS['client_info']) {
    return this.getAppContextByType<TRESPONSE_PRE_SETTINGS>(appMaster, clientInfo, 'app');
  }
  getFinalizedAppData(appMaster: TRESPONSE_APP_MASTER, clientInfo: TRESPONSE_TENANT_APPS['client_info']) {
    return this.getAppContextByType<TRESPONSE_PRE_SETTINGS>(appMaster, clientInfo, 'bctx');
  }

  public registerAgentApp(token: string, appWithSettings: TPreSettingCheckedList) {
    const appsArr = appWithSettings.map((v) => ({ app_id: v.appInfo?.app_id, appName: v.appInfo?.app_name }));
    const url = this.envProvider.getLicenceUrl('ACTIVATE_APP');
    const device = this.deviceId;
    const date = new Date();
    const stringTz = tzdetect.matches('9999')[0]; // Intl.DateTimeFormat().resolvedOptions().timeZone;
    const postData: TREQUEST_REGISTERED_APP = {
      act_tkn: token,
      apps: appsArr,
      mac_add: device,
      timezone: stringTz,
      timezoneOffset: date.getTimezoneOffset(),
    };
    return this.http.post<TRESPONSE_REGISTERED_APP>(url, JSON.stringify(postData), {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    });
  }

  public acknowledgeAgent(
    token: string,
    activeTokens: TRESPONSE_REGISTERED_APP['tokens'],
    appMaster: TRESPONSE_APP_MASTER,
    type: 'new' | 'update',
  ) {
    const url = this.envProvider.getLicenceUrl('CLIENT_PROFILE_ACK');
    const deviceMac = this.deviceId;
    const timestamp = new Date();
    const installedApps = appMaster.apps.map((v) => v.product_type).join(',');
    return this.http.post(
      url,
      JSON.stringify({
        act_tkn: token,
        device_info: {
          mac_add: deviceMac,
          os: this.deviceInfo.platform || 'web',
          os_version: this.deviceInfo.osVersion || 'web',
          browser_type: 'Chrome',
          browser_version: this.deviceInfo.webViewVersion || '0.0.0',
          model: this.deviceInfo.model || 'web',
          manufacturer: this.deviceInfo.manufacturer || 'web',
          serial_no: deviceMac,
          app_version: BuildUtils.APP_VERSION,
          apps: installedApps,
          datetime: timestamp.toISOString(),
          offset: timestamp.getTimezoneOffset(),
        },
        app_configs: activeTokens.map((token) => {
          const decodedToken = new JwtHelperService().decodeToken(token.token);
          const newTokenInfo = {
            ...token,
            license_key: decodedToken['license_key'],
          };
          return _.omit(newTokenInfo, 'token');
        }),
        type: type,
      }),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      },
    );
  }

  public getAppUpdates(payload: TREQUEST_GET_UPDATES) {
    const url = this.envProvider.getAppUpdatesURL('APP_UPDATES');
    return this.http.post<TRESPONSE_GET_UPDATES>(url, JSON.stringify(payload), {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    });
  }

  async getDeviceIdFromPlugin() {
    try {
      const { deviceId } = await DeviceId.getAndroidId();
      console.log('deviceId:', deviceId);
      return { identifier: deviceId };
    } catch (err) {
      console.error('Failed to get Device ID', err);
      return null;
    }
  }

  async init() {
    const pluginDeviceId = await this.getDeviceIdFromPlugin();
    const [deviceInfo, deviceIdentifier] = await Promise.all([
      Device.getInfo(),
      pluginDeviceId ? pluginDeviceId : Device.getId(),
    ]);
    this.deviceInfo = deviceInfo;
    this.deviceId = `${deviceInfo.model}_${deviceIdentifier.identifier}`;
  }
}
